// Types and pure formatting helpers for the live Pro plan prices shown on
// /pricing. Kept dependency-free (no Preact, no fetch) so `node --test` can
// cover the formatting directly — see pro-plan-pricing.test.ts.

interface PlanOption {
  priceId: string;
  unitAmount: number;
  currency: string;
  interval: 'month' | 'year';
}

export interface ProPlanOptions {
  monthly: PlanOption;
  yearly: PlanOption;
}

// Stripe's `unit_amount` is in the smallest currency unit — cents for USD,
// but a whole unit for these currencies (mirrors webapp's Subscription.tsx
// formatMoney; kept in sync manually since neither side imports the other).
const ZERO_DECIMAL_CURRENCIES = new Set([
  'jpy', 'krw', 'vnd', 'clp', 'pyg', 'rwf', 'ugx', 'xaf', 'xof', 'bif', 'djf', 'gnf', 'kmf', 'mga', 'xpf'
]);

// Formats a Stripe unit amount as a locale-correct currency string. Whole
// amounts render without decimals ("$12" rather than "$12.00") — the norm
// for a marketing pricing page, as opposed to an invoice.
export function formatUnitAmount(unitAmount: number, currency: string, locale: string): string {
  const lower = currency.toLowerCase();
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(lower);
  const amount = isZeroDecimal ? unitAmount : unitAmount / 100;
  const hasFraction = amount % 1 !== 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: isZeroDecimal ? 0 : (hasFraction ? 2 : 0),
      maximumFractionDigits: isZeroDecimal ? 0 : 2
    }).format(amount);
  } catch {
    return `${amount.toFixed(hasFraction ? 2 : 0)} ${currency.toUpperCase()}`;
  }
}

// The yearly Price is a per-seat annual total, not a monthly rate. Dividing
// by 12 gives the equivalent monthly rate for an "or $X/mo billed yearly"
// comparison against the monthly Price's headline amount.
export function yearlyMonthlyEquivalent(yearlyUnitAmount: number): number {
  return Math.round(yearlyUnitAmount / 12);
}
